import type { DocumentChangeAuditRepository } from "../../domain/repositories/DocumentChangeAuditRepository";
import type {
  DocumentChangeAuditQueryParams,
  DocumentChangeAuditsResultEntity,
  DocumentChangeReportSummaryEntity,
} from "../../domain/entities/DocumentChangeAuditEntity";
import {
  getDocumentChangeAudits,
  getDocumentChangeReportSummary,
} from "../api/documentChangeAuditApi";
import { DocumentChangeAuditMapper } from "../mappers/DocumentChangeAuditMapper";
import type { DocumentChangeAuditQueryParamsDTO } from "../models/DocumentChangeAuditDTOs";

function toQueryParamsDTO(
  params?: DocumentChangeAuditQueryParams,
): DocumentChangeAuditQueryParamsDTO | undefined {
  if (!params) return undefined;

  const dto: DocumentChangeAuditQueryParamsDTO = {
    limit: params.limit,
    offset: params.offset,
    search: params.search,
    date_from: params.dateFrom,
    date_to: params.dateTo,
    posted_date_from: params.postedDateFrom,
    posted_date_to: params.postedDateTo,
  };

  if (params.entityType) {
    dto.entity_type = params.entityType;
  }

  return dto;
}

export class DocumentChangeAuditRepositoryImpl
  implements DocumentChangeAuditRepository
{
  async getAudits(
    params?: DocumentChangeAuditQueryParams,
  ): Promise<DocumentChangeAuditsResultEntity> {
    const response = await getDocumentChangeAudits(toQueryParamsDTO(params));

    if (!response.success || !response.data) {
      throw new Error(
        response.message || "Failed to fetch document change audits",
      );
    }

    return DocumentChangeAuditMapper.toAuditsResultEntity(
      response.data.audits,
      response.data.summary,
      response.data.pagination,
    );
  }

  async getReportSummary(
    params?: DocumentChangeAuditQueryParams,
  ): Promise<DocumentChangeReportSummaryEntity> {
    const response = await getDocumentChangeReportSummary(
      toQueryParamsDTO(params),
    );

    if (!response.success || !response.data) {
      throw new Error(
        response.message || "Failed to fetch document change report summary",
      );
    }

    return DocumentChangeAuditMapper.toSummaryEntity(response.data);
  }
}
